import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import {
  Play, Pause, RotateCcw, Gauge, AlertTriangle, ShieldCheck,
  Clock, Database, User, Box, Layers, Building2, ChevronRight, FileSearch,
  Link2, Check,
} from 'lucide-react';

/**
 * InvestigationReplay — "Watchable" scrubber for INV-2026-042.
 * Renders inside the Investigations tab. Reads `inv.timeline` from seed.
 * Five chronological phases drive a stage-panel animation, exposure counters,
 * and an event-detail card. Pure CSS + framer-motion. No new page, no new route.
 */

// ---- Stage choreography ----
// Each phase corresponds to one CloudTrail event in the timeline.
// node states change over time; edges activate to tell the contamination story.
const STAGE_NODES = [
  { id: 'dev-role',  label: 'developer-console-access', sub: 'DEV · IAM identity', icon: User,       col: 0, row: 0 },
  { id: 'dev-acct',  label: 'stratum-dev-sandbox',      sub: 'DEV ACCOUNT',         icon: Building2,  col: 0, row: 1 },
  { id: 'ecr',       label: 'payments-api:latest',      sub: 'ECR · dev-origin',    icon: Box,        col: 1, row: 0 },
  { id: 'fargate',   label: 'stratum-payments-api',     sub: 'PROD · Fargate',      icon: Layers,     col: 2, row: 0 },
  { id: 'prod-acct', label: 'stratum-payments-prod',    sub: 'PROD ACCOUNT · PCI',  icon: Building2,  col: 2, row: 1 },
  { id: 'pci-data',  label: 'pci-customer-data',        sub: 'S3 · highly confidential', icon: Database, col: 3, row: 0 },
];

// At each phase index, which nodes are "lit" and which edges are "active"?
// State per node: 'idle' | 'watch' (warning) | 'breach' (critical) | 'clean' (resolved)
// State per edge: 'idle' | 'forming' (dashed amber) | 'active' (solid red) | 'traversed' (pulsing red)
const PHASE_STATES = [
  { // Phase 0 — Pre-incident
    nodes: { 'dev-role': 'idle', 'dev-acct': 'idle', 'ecr': 'idle', 'fargate': 'idle', 'prod-acct': 'idle', 'pci-data': 'idle' },
    edges: {},
    label: 'Baseline',
    sub: 'No incident · all controls reporting',
  },
  { // Phase 1 — IAM trust path created (Oct 3 2025)
    nodes: { 'dev-role': 'idle', 'dev-acct': 'watch', 'ecr': 'idle', 'fargate': 'idle', 'prod-acct': 'watch', 'pci-data': 'idle' },
    edges: { 'trust-path': 'forming' },
    label: 'Trust path created',
    sub: 'No approved baseline · no change ticket',
  },
  { // Phase 2 — Image pushed to shared ECR (Nov 28 2025)
    nodes: { 'dev-role': 'watch', 'dev-acct': 'watch', 'ecr': 'watch', 'fargate': 'idle', 'prod-acct': 'watch', 'pci-data': 'idle' },
    edges: { 'trust-path': 'forming', 'role-push': 'forming' },
    label: 'Image pushed to shared ECR',
    sub: 'developer-console-access · no production pipeline',
  },
  { // Phase 3 — Image deployed to prod Fargate (Feb 14 2026)
    nodes: { 'dev-role': 'watch', 'dev-acct': 'watch', 'ecr': 'watch', 'fargate': 'breach', 'prod-acct': 'breach', 'pci-data': 'watch' },
    edges: { 'trust-path': 'forming', 'role-push': 'forming', 'ecr-deploy': 'active' },
    label: 'Dev image deployed to PRODUCTION',
    sub: 'No Terraform plan · no PR · no ServiceNow ticket',
  },
  { // Phase 4 — Trust traversed (Apr 14 2026 02:17 UTC)
    nodes: { 'dev-role': 'breach', 'dev-acct': 'breach', 'ecr': 'watch', 'fargate': 'breach', 'prod-acct': 'breach', 'pci-data': 'breach' },
    edges: { 'trust-path': 'traversed', 'role-push': 'forming', 'ecr-deploy': 'active', 'data-access': 'traversed' },
    label: 'Trust path traversed · PCI data accessed',
    sub: 'AssumeRole at 02:17 UTC · 3 S3 GetObject on PCI bucket',
  },
  { // Phase 5 — Missing evidence detected (Apr 21 2026)
    nodes: { 'dev-role': 'breach', 'dev-acct': 'breach', 'ecr': 'watch', 'fargate': 'breach', 'prod-acct': 'breach', 'pci-data': 'breach' },
    edges: { 'trust-path': 'traversed', 'role-push': 'forming', 'ecr-deploy': 'active', 'data-access': 'traversed' },
    label: 'Continuous evidence stopped',
    sub: 'Config evaluations silent for 9 days · investigation opened',
  },
];

// Edge geometry — connect by node id pairs
const STAGE_EDGES = [
  { id: 'trust-path',  from: 'dev-acct',  to: 'prod-acct' },
  { id: 'role-push',   from: 'dev-role',  to: 'ecr' },
  { id: 'ecr-deploy',  from: 'ecr',       to: 'fargate' },
  { id: 'data-access', from: 'fargate',   to: 'pci-data' },
];

// Layout
const STAGE_W = 880;
const STAGE_H = 320;
const COL_X = [70, 290, 510, 740];
const ROW_Y = [70, 220];
const NODE_W = 168;
const NODE_H = 60;

const NODE_TONE = {
  idle:   { ring: '#E2E8F0', fill: '#FFFFFF', text: '#475569', glow: 'none' },
  watch:  { ring: '#F59E0B', fill: '#FFFBEB', text: '#92400E', glow: '0 0 0 4px rgba(245,158,11,0.10)' },
  breach: { ring: '#DC2626', fill: '#FEF2F2', text: '#991B1B', glow: '0 0 0 5px rgba(220,38,38,0.14)' },
  clean:  { ring: '#10B981', fill: '#ECFDF5', text: '#065F46', glow: '0 0 0 4px rgba(16,185,129,0.12)' },
};

const EDGE_TONE = {
  idle:      { stroke: '#E2E8F0', width: 1.25, dash: '0',    opacity: 0.5 },
  forming:   { stroke: '#F59E0B', width: 1.5,  dash: '5 4',  opacity: 0.85 },
  active:    { stroke: '#DC2626', width: 2,    dash: '0',    opacity: 1 },
  traversed: { stroke: '#DC2626', width: 2.25, dash: '0',    opacity: 1 },
};

function nodeRect(n) {
  return {
    x: COL_X[n.col],
    y: ROW_Y[n.row],
    cx: COL_X[n.col] + NODE_W / 2,
    cy: ROW_Y[n.row] + NODE_H / 2,
    right: COL_X[n.col] + NODE_W,
    bottom: ROW_Y[n.row] + NODE_H,
  };
}

function edgePath(eFrom, eTo) {
  const a = nodeRect(eFrom);
  const b = nodeRect(eTo);
  // pick endpoints from node edge that face the target
  let sx = a.cx, sy = a.cy, tx = b.cx, ty = b.cy;
  if (a.col !== eFrom.col) { /* same node */ }
  if (eFrom.col === eTo.col) {
    // vertical
    sx = a.cx; tx = b.cx;
    sy = a.row < eTo.row ? a.bottom : a.y;
    ty = a.row < eTo.row ? b.y : b.bottom;
  } else {
    sx = a.col < eTo.col ? a.right : a.x;
    tx = a.col < eTo.col ? b.x : b.right;
    sy = a.cy; ty = b.cy;
  }
  // Bezier curve
  const dx = (tx - sx) * 0.5;
  const c1x = sx + dx, c1y = sy;
  const c2x = tx - dx, c2y = ty;
  return `M ${sx},${sy} C ${c1x},${c1y} ${c2x},${c2y} ${tx},${ty}`;
}

const SPEEDS = [0.5, 1, 2, 4];
// Base milliseconds per phase advance (at 1x speed)
const BASE_MS = 1900;

// Cumulative dwell-days at each phase (matches timeline dates)
// 2025-10-03 = day 0; 2025-11-28 = +56; 2026-02-14 = +134; 2026-04-14 = +193; 2026-04-21 = +200
const PHASE_DWELL_DAYS = [0, 0, 56, 134, 193, 200];
// Records-at-risk (animated counter) escalating per phase
const PHASE_PCI_RISK   = [0, 0, 0, 12_400, 184_700, 184_700];

// Phase dates (mirrors PHASES on GovernanceGraphFull) for the Governance State panel header
const PHASE_DATES = [
  'Sep 26, 2025',
  'Oct 3, 2025',
  'Nov 28, 2025',
  'Feb 14, 2026',
  'Apr 14, 2026 · 02:17 UTC',
  'Apr 21, 2026',
];

// Live governance state at each phase — drives the right-side flight-recorder panel
const PHASE_GOV_STATE = [
  { // Phase 0 — Baseline
    controls: [
      { id: 'scp-s3',             label: 'SCP: Deny S3 Public Access',           status: 'enforced' },
      { id: 'config-enc',         label: 'Config: Encryption at Rest',           status: 'enforced' },
      { id: 'guardrail-baseline', label: 'Control Tower: Baseline',              status: 'enforced' },
      { id: 'change-mgmt',        label: 'Change Management: Pipeline Only',     status: 'enforced' },
      { id: 'ecr-immutable',      label: 'ECR: Tag Immutability',                status: 'enforced' },
    ],
    exposure: null,
    evidence_count: 0,
    verdict: null,
  },
  { // Phase 1 — Trust path created
    controls: [
      { id: 'scp-s3',             label: 'SCP: Deny S3 Public Access',           status: 'enforced' },
      { id: 'config-enc',         label: 'Config: Encryption at Rest',           status: 'enforced' },
      { id: 'guardrail-baseline', label: 'Control Tower: Baseline',              status: 'enforced' },
      { id: 'change-mgmt',        label: 'Change Management: Pipeline Only',     status: 'gap', gap_since: 'Oct 3, 2025' },
      { id: 'ecr-immutable',      label: 'ECR: Tag Immutability',                status: 'enforced' },
    ],
    exposure: { label: 'Cross-environment trust path · 0 traversals so far', days: 0, severity: 'warning' },
    evidence_count: 3,
    verdict: null,
  },
  { // Phase 2 — Image pushed
    controls: [
      { id: 'scp-s3',             label: 'SCP: Deny S3 Public Access',           status: 'enforced' },
      { id: 'config-enc',         label: 'Config: Encryption at Rest',           status: 'enforced' },
      { id: 'guardrail-baseline', label: 'Control Tower: Baseline',              status: 'enforced' },
      { id: 'change-mgmt',        label: 'Change Management: Pipeline Only',     status: 'gap', gap_since: 'Oct 3, 2025' },
      { id: 'ecr-immutable',      label: 'ECR: Tag Immutability',                status: 'gap', gap_since: 'Nov 28, 2025' },
    ],
    exposure: { label: 'Unapproved image in shared ECR · 56 days undetected', days: 56, severity: 'warning' },
    evidence_count: 8,
    verdict: null,
  },
  { // Phase 3 — Deployed to production
    controls: [
      { id: 'scp-s3',             label: 'SCP: Deny S3 Public Access',           status: 'enforced' },
      { id: 'config-enc',         label: 'Config: Encryption at Rest',           status: 'enforced' },
      { id: 'guardrail-baseline', label: 'Control Tower: Baseline',              status: 'gap', gap_since: 'Feb 14, 2026' },
      { id: 'change-mgmt',        label: 'Change Management: Pipeline Only',     status: 'gap', gap_since: 'Oct 3, 2025' },
      { id: 'ecr-immutable',      label: 'ECR: Tag Immutability',                status: 'gap', gap_since: 'Nov 28, 2025' },
    ],
    exposure: { label: 'Dev image in prod · PCI workload · 59 days', days: 59, severity: 'critical' },
    evidence_count: 16,
    verdict: 'Production running unapproved artifact · 3 controls in gap state',
  },
  { // Phase 4 — Traversal
    controls: [
      { id: 'scp-s3',             label: 'SCP: Deny S3 Public Access',           status: 'enforced' },
      { id: 'config-enc',         label: 'Config: Encryption at Rest',           status: 'gap', gap_since: 'Apr 12, 2026' },
      { id: 'guardrail-baseline', label: 'Control Tower: Baseline',              status: 'gap', gap_since: 'Feb 14, 2026' },
      { id: 'change-mgmt',        label: 'Change Management: Pipeline Only',     status: 'gap', gap_since: 'Oct 3, 2025' },
      { id: 'ecr-immutable',      label: 'ECR: Tag Immutability',                status: 'gap', gap_since: 'Nov 28, 2025' },
    ],
    exposure: { label: 'PCI data accessed during 4-control gap · 2 AssumeRole traversals', days: 201, severity: 'critical' },
    evidence_count: 23,
    verdict: 'BREACH: Trust traversal + PCI access during multi-control gap · $184,700 governance debt',
  },
  { // Phase 5 — Detected
    controls: [
      { id: 'scp-s3',             label: 'SCP: Deny S3 Public Access',           status: 'enforced' },
      { id: 'config-enc',         label: 'Config: Encryption at Rest',           status: 'gap', gap_since: 'Apr 12, 2026' },
      { id: 'guardrail-baseline', label: 'Control Tower: Baseline',              status: 'gap', gap_since: 'Feb 14, 2026' },
      { id: 'change-mgmt',        label: 'Change Management: Pipeline Only',     status: 'gap', gap_since: 'Oct 3, 2025' },
      { id: 'ecr-immutable',      label: 'ECR: Tag Immutability',                status: 'gap', gap_since: 'Nov 28, 2025' },
    ],
    exposure: { label: 'Continuous evidence stopped · investigation opened', days: 201, severity: 'critical' },
    evidence_count: 28,
    verdict: 'BREACH: Trust traversal + PCI access during multi-control gap · $184,700 governance debt',
  },
];

export default function InvestigationReplay({ inv }) {
  const timeline = inv?.timeline ?? [];
  const totalPhases = timeline.length + 1; // +1 for the baseline pre-incident phase
  const [searchParams, setSearchParams] = useSearchParams();
  const initialPhase = (() => {
    const p = parseInt(searchParams.get('replay'), 10);
    return Number.isFinite(p) && p >= 0 && p < totalPhases ? p : 0;
  })();
  const initialAutoplay = searchParams.get('autoplay') === '1';
  const [phase, setPhase]       = useState(initialPhase);
  const [playing, setPlaying]   = useState(initialAutoplay);
  const [speed, setSpeed]       = useState(1);
  const [copied, setCopied]     = useState(false);
  const tickRef = useRef(null);

  // Sync phase to URL silently (no history pollution; baseline phase 0 strips param)
  useEffect(() => {
    const next = new URLSearchParams(searchParams);
    if (phase === 0) {
      next.delete('replay');
    } else {
      next.set('replay', String(phase));
    }
    // strip autoplay once consumed
    if (next.get('autoplay')) next.delete('autoplay');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase]);

  // Auto-advance loop
  useEffect(() => {
    if (!playing) return;
    if (phase >= totalPhases - 1) { setPlaying(false); return; }
    tickRef.current = setTimeout(() => {
      setPhase((p) => Math.min(p + 1, totalPhases - 1));
    }, BASE_MS / speed);
    return () => clearTimeout(tickRef.current);
  }, [playing, phase, speed, totalPhases]);

  const reset = () => {
    clearTimeout(tickRef.current);
    setPhase(0);
    setPlaying(false);
  };

  const jumpTo = (p) => {
    clearTimeout(tickRef.current);
    setPhase(p);
    setPlaying(false);
  };

  const togglePlay = () => {
    if (phase >= totalPhases - 1) {
      setPhase(0);
      setPlaying(true);
      return;
    }
    setPlaying((p) => !p);
  };

  const copyShareLink = async () => {
    const url = new URL(window.location.href);
    if (phase === 0) url.searchParams.delete('replay');
    else url.searchParams.set('replay', String(phase));
    url.searchParams.delete('autoplay'); // share the static state, not autoplay
    try {
      await navigator.clipboard.writeText(url.toString());
      setCopied(true);
      const phaseLabel = phase === 0 ? 'baseline' : (timeline[phase - 1]?.title || `phase ${phase}`);
      toast.success('Replay link copied', {
        description: `Opens at ${phaseLabel}`,
        duration: 2400,
      });
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy to clipboard');
    }
  };

  const state = PHASE_STATES[Math.min(phase, PHASE_STATES.length - 1)];
  const currentEvent = phase === 0 ? null : timeline[phase - 1];
  const dwell = PHASE_DWELL_DAYS[Math.min(phase, PHASE_DWELL_DAYS.length - 1)];
  const pciRisk = PHASE_PCI_RISK[Math.min(phase, PHASE_PCI_RISK.length - 1)];

  // pre-build node lookup
  const nodeMap = useMemo(() => Object.fromEntries(STAGE_NODES.map((n) => [n.id, n])), []);

  return (
    <div data-testid="investigation-replay" className="space-y-4">
      {/* HEADER */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 bg-white border border-slate-200 rounded-lg p-5">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.14em] text-apex-lens">▎Watch how this happened</div>
          <h3 className="text-base font-bold text-apex-ink mt-1">Replay · INV-2026-042</h3>
          <p className="text-xs text-apex-neutral mt-1 max-w-xl leading-relaxed">
            Five CloudTrail events compressed into a 10-second scrubber. Hit play to watch the cross-environment trust path become a PCI breach in real chronological order — every node lights up exactly when it was touched.
          </p>
        </div>
        <div className="flex items-center gap-4 sm:gap-6">
          <Stat
            testId="replay-stat-dwell"
            label="Days exposed"
            value={dwell}
            tone={dwell > 100 ? 'critical' : dwell > 0 ? 'warning' : 'neutral'}
            icon={<Clock className="w-3.5 h-3.5" />}
          />
          <Stat
            testId="replay-stat-pci"
            label="PCI records reachable"
            value={pciRisk.toLocaleString()}
            tone={pciRisk > 0 ? 'critical' : 'neutral'}
            icon={<Database className="w-3.5 h-3.5" />}
          />
          <Stat
            testId="replay-stat-phase"
            label="Phase"
            value={`${phase}/${totalPhases - 1}`}
            tone={phase >= totalPhases - 1 ? 'critical' : 'neutral'}
            icon={<FileSearch className="w-3.5 h-3.5" />}
          />
        </div>
      </div>

      {/* PCI exposure counter — phase-driven financial impact */}
      <motion.div
        className="flex items-center justify-between px-4 py-2.5 rounded-lg border"
        initial={false}
        animate={{
          borderColor: phase >= 4 ? '#DC2626' : phase >= 3 ? '#D97706' : '#E2E8F0',
          backgroundColor: phase >= 4 ? 'rgba(220,38,38,0.06)' : phase >= 3 ? 'rgba(217,119,6,0.06)' : '#F8FAFC',
        }}
        transition={{ duration: 0.5 }}
        data-testid="pci-exposure-counter"
      >
        <div>
          <div className="text-[10px] uppercase tracking-wider font-mono text-apex-neutral">
            PCI data exposure risk accrued
          </div>
          <div className="text-[11px] text-apex-neutral font-mono mt-0.5">
            {phase < 3 ? 'No PCI-scope workload exposure' : 'Live production PCI data at risk'}
          </div>
        </div>
        <motion.div
          key={pciRisk}
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-right"
        >
          <div
            className="text-xl font-bold tabular-nums font-mono"
            style={{ color: phase >= 4 ? '#DC2626' : phase >= 3 ? '#D97706' : '#64748B' }}
          >
            ${pciRisk.toLocaleString()}
          </div>
          {phase >= 3 && (
            <div className="text-[10px] font-mono text-apex-neutral">governance debt accrued</div>
          )}
        </motion.div>
      </motion.div>

      {/* Two-column flight-recorder layout: stage (left) + live governance state (right) */}
      <div className="grid grid-cols-1 md:grid-cols-[3fr_2fr] gap-4">
        {/* STAGE PANEL */}
        <div data-testid="replay-stage" className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5 relative overflow-hidden">
        {/* Subtle grid backdrop */}
        <svg className="absolute inset-0 w-full h-full opacity-[0.35] pointer-events-none" aria-hidden>
          <defs>
            <pattern id="replay-grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="#F1F5F9" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#replay-grid)" />
        </svg>

        <div className="relative">
          <div className="hidden md:block">
            <svg viewBox={`0 0 ${STAGE_W} ${STAGE_H}`} className="w-full h-[300px]" preserveAspectRatio="xMidYMid meet">
              {/* edges */}
              {STAGE_EDGES.map((e) => {
                const eState = state.edges[e.id] || 'idle';
                const tone = EDGE_TONE[eState];
                const path = edgePath(nodeMap[e.from], nodeMap[e.to]);
                const isPulse = eState === 'traversed';
                return (
                  <g key={e.id} data-testid={`replay-edge-${e.id}`}>
                    <motion.path
                      d={path}
                      fill="none"
                      stroke={tone.stroke}
                      strokeWidth={tone.width}
                      strokeDasharray={tone.dash}
                      strokeLinecap="round"
                      initial={false}
                      animate={{ opacity: tone.opacity, pathLength: 1 }}
                      transition={{ duration: 0.6, ease: 'easeInOut' }}
                    />
                    {isPulse && (
                      <motion.circle
                        r="4"
                        fill="#DC2626"
                        initial={{ offsetDistance: '0%', opacity: 0.9 }}
                        animate={{ offsetDistance: '100%', opacity: [0.9, 0.3, 0.9] }}
                        transition={{ duration: 1.6 / speed, repeat: Infinity, ease: 'linear' }}
                        style={{ offsetPath: `path("${path}")` }}
                      />
                    )}
                  </g>
                );
              })}

              {/* nodes */}
              {STAGE_NODES.map((n) => {
                const r = nodeRect(n);
                const ns = state.nodes[n.id] || 'idle';
                const tone = NODE_TONE[ns];
                const Icon = n.icon;
                return (
                  <g key={n.id} data-testid={`replay-node-${n.id}`} transform={`translate(${r.x},${r.y})`}>
                    {/* glow rect */}
                    {ns === 'breach' && (
                      <motion.rect
                        x="-4" y="-4" width={NODE_W + 8} height={NODE_H + 8} rx="10"
                        fill="rgba(220,38,38,0.06)"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: [0.45, 0.85, 0.45] }}
                        transition={{ duration: 1.6 / speed, repeat: Infinity }}
                      />
                    )}
                    <motion.rect
                      width={NODE_W} height={NODE_H} rx="8"
                      fill={tone.fill}
                      stroke={tone.ring}
                      strokeWidth="1.5"
                      initial={false}
                      animate={{ scale: ns === 'breach' ? [1, 1.03, 1] : 1 }}
                      transition={{ duration: 0.45, ease: 'easeOut' }}
                      style={{ originX: 0.5, originY: 0.5, transformBox: 'fill-box' }}
                    />
                    <foreignObject x="10" y="8" width={NODE_W - 20} height={NODE_H - 14}>
                      <div className="flex items-start gap-2 h-full" xmlns="http://www.w3.org/1999/xhtml">
                        <div
                          className="w-7 h-7 rounded-md flex items-center justify-center shrink-0"
                          style={{ backgroundColor: tone.fill, border: `1px solid ${tone.ring}` }}
                        >
                          <Icon style={{ color: tone.text }} size={14} strokeWidth={1.8} />
                        </div>
                        <div className="min-w-0">
                          <div className="text-[12px] font-semibold leading-tight truncate" style={{ color: tone.text }}>{n.label}</div>
                          <div className="text-[10px] leading-tight truncate text-apex-neutral mt-0.5">{n.sub}</div>
                        </div>
                      </div>
                    </foreignObject>
                  </g>
                );
              })}
            </svg>
          </div>

          {/* Mobile fallback — chain-list summary of node states */}
          <div className="md:hidden space-y-2 py-2" data-testid="replay-stage-mobile">
            {STAGE_NODES.map((n) => {
              const ns = state.nodes[n.id] || 'idle';
              const tone = NODE_TONE[ns];
              const Icon = n.icon;
              return (
                <div
                  key={n.id}
                  className="flex items-center gap-3 p-2 rounded-md border"
                  style={{ borderColor: tone.ring, backgroundColor: tone.fill }}
                >
                  <Icon style={{ color: tone.text }} size={14} strokeWidth={1.8} />
                  <div className="min-w-0 flex-1">
                    <div className="text-[12px] font-semibold truncate" style={{ color: tone.text }}>{n.label}</div>
                    <div className="text-[10px] truncate text-apex-neutral">{n.sub}</div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider font-mono" style={{ color: tone.text }}>{ns}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Active event card */}
        <div className="mt-4 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3 items-stretch">
          <AnimatePresence mode="wait">
            <motion.div
              key={phase}
              data-testid="replay-event-card"
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              transition={{ duration: 0.3 }}
              className="border border-slate-200 rounded-md bg-slate-50 px-4 py-3"
            >
              <div className="flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider">
                {currentEvent ? (
                  <>
                    <AlertTriangle size={12} className={currentEvent.tag === 'critical' ? 'text-apex-critical' : 'text-amber-600'} />
                    <span className={currentEvent.tag === 'critical' ? 'text-apex-critical' : 'text-amber-700'}>
                      {currentEvent.tag === 'critical' ? 'CRITICAL EVENT' : 'WATCH EVENT'}
                    </span>
                    <span className="text-slate-400">·</span>
                    <span className="text-slate-500">{currentEvent.date}</span>
                  </>
                ) : (
                  <>
                    <ShieldCheck size={12} className="text-emerald-600" />
                    <span className="text-emerald-700">PRE-INCIDENT BASELINE</span>
                  </>
                )}
              </div>
              <div className="text-[13.5px] font-semibold text-apex-ink mt-1">
                {currentEvent ? currentEvent.title : 'No anomalies · controls reporting normally'}
              </div>
              {currentEvent?.detail && (
                <div className="text-xs text-apex-neutral mt-1 whitespace-pre-line leading-relaxed">{currentEvent.detail}</div>
              )}
              {currentEvent?.trail && (
                <div className="mt-2 inline-flex items-center gap-1.5 text-[10.5px] font-mono text-apex-lens bg-white border border-slate-200 rounded px-2 py-0.5">
                  <span className="text-slate-400">CloudTrail:</span>
                  {currentEvent.trail}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
          <div className="hidden md:flex flex-col justify-between border border-slate-200 rounded-md bg-white px-4 py-3 min-w-[180px]">
            <div className="text-[10px] font-mono uppercase tracking-wider text-apex-neutral">Stage</div>
            <div className="text-sm font-bold text-apex-ink mt-1 leading-tight">{state.label}</div>
            <div className="text-[11px] text-apex-neutral leading-snug mt-1">{state.sub}</div>
          </div>
        </div>
      </div>

        {/* GOVERNANCE STATE PANEL — flight-recorder right column */}
        <GovernanceStatePanel phase={phase} />
      </div>

      {/* TRANSPORT */}
      <div data-testid="replay-controls" className="bg-white border border-slate-200 rounded-lg p-4 sm:p-5">
        <Scrubber
          phase={phase}
          totalPhases={totalPhases}
          timeline={timeline}
          onJump={jumpTo}
        />
        <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2">
            <button
              data-testid="replay-play"
              onClick={togglePlay}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-apex-lens text-white text-xs font-semibold hover:bg-apex-lens-dark transition"
            >
              {playing ? <Pause size={13} /> : <Play size={13} />}
              {playing ? 'Pause' : phase >= totalPhases - 1 ? 'Replay' : 'Play'}
            </button>
            <button
              data-testid="replay-reset"
              onClick={reset}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md border border-slate-200 text-apex-ink text-xs font-semibold hover:bg-slate-50 transition"
            >
              <RotateCcw size={12} />
              Reset
            </button>
            <button
              data-testid="replay-share"
              onClick={copyShareLink}
              title="Copy a deep-link that opens this exact phase"
              className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-md border text-xs font-semibold transition ${copied ? 'border-emerald-300 bg-emerald-50 text-emerald-700' : 'border-slate-200 text-apex-ink hover:bg-slate-50'}`}
            >
              {copied ? <Check size={12} /> : <Link2 size={12} />}
              {copied ? 'Copied' : 'Share replay'}
            </button>
          </div>
          <div className="flex items-center gap-2" data-testid="replay-speed">
            <Gauge size={13} className="text-apex-neutral" />
            <span className="text-[11px] font-mono uppercase tracking-wider text-apex-neutral">Speed</span>
            <div className="flex items-center bg-slate-50 border border-slate-200 rounded-md p-0.5">
              {SPEEDS.map((s) => (
                <button
                  key={s}
                  data-testid={`replay-speed-${s}`}
                  onClick={() => setSpeed(s)}
                  className={`text-[11px] font-mono px-2 h-6 rounded ${speed === s ? 'bg-white shadow-sm text-apex-ink font-semibold' : 'text-apex-neutral hover:text-apex-ink'}`}
                >
                  {s}x
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---- Sub-components ----
function Stat({ label, value, icon, tone = 'neutral', testId }) {
  const TONE_CLASS = {
    neutral:  'text-slate-500',
    warning:  'text-amber-700',
    critical: 'text-apex-critical',
  };
  return (
    <div data-testid={testId} className="flex flex-col">
      <div className={`text-[10px] uppercase font-mono tracking-wider flex items-center gap-1 ${TONE_CLASS[tone]}`}>
        {icon}
        {label}
      </div>
      <motion.div
        key={`${value}`}
        initial={{ opacity: 0, y: 4 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className={`text-lg font-bold tabular-nums ${tone === 'critical' ? 'text-apex-critical' : tone === 'warning' ? 'text-amber-700' : 'text-apex-ink'}`}
      >
        {value}
      </motion.div>
    </div>
  );
}

function GovernanceStatePanel({ phase }) {
  const idx = Math.max(0, Math.min(phase, PHASE_GOV_STATE.length - 1));
  const state = PHASE_GOV_STATE[idx];
  return (
    <motion.div
      className="bg-white border border-slate-200 rounded-lg p-4 space-y-4"
      data-testid="governance-state-panel"
    >
      {/* Panel header */}
      <div className="flex items-center justify-between">
        <div className="min-w-0">
          <div className="text-[10px] uppercase tracking-wider font-mono text-apex-neutral">
            Governance state at this moment
          </div>
          <div className="text-[11px] font-mono text-apex-ink mt-0.5 truncate">
            {PHASE_DATES[idx] ?? '—'}
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="w-1.5 h-1.5 rounded-full bg-apex-success animate-pulse" />
          <span className="text-[10px] font-mono text-apex-neutral">Live reconstruction</span>
        </div>
      </div>

      {/* Control coverage */}
      <div className="space-y-1.5">
        <div className="text-[10px] uppercase tracking-wider font-mono text-apex-neutral mb-2">
          Control coverage
        </div>
        {state.controls.map((ctrl) => (
          <motion.div
            key={ctrl.id}
            layout
            initial={{ opacity: 0, x: -4 }}
            animate={{ opacity: 1, x: 0 }}
            className="flex items-center gap-2"
            data-testid={`gov-control-${ctrl.id}`}
          >
            <motion.span
              animate={{ backgroundColor: ctrl.status === 'enforced' ? '#059669' : '#DC2626' }}
              transition={{ duration: 0.4 }}
              className="w-2 h-2 rounded-full shrink-0"
            />
            <span className={`text-[11px] flex-1 ${ctrl.status === 'gap' ? 'text-apex-critical' : 'text-apex-ink'}`}>
              {ctrl.label}
            </span>
            {ctrl.status === 'gap' && (
              <motion.span
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                className="text-[9px] font-mono text-apex-critical bg-red-50 border border-red-200 rounded px-1 py-0.5 shrink-0"
              >
                GAP
              </motion.span>
            )}
          </motion.div>
        ))}
      </div>

      {/* Exposure window */}
      {state.exposure && (
        <motion.div
          key={`exp-${idx}`}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className={`rounded-lg border p-3 ${
            state.exposure.severity === 'critical'
              ? 'bg-red-50 border-red-200'
              : 'bg-amber-50 border-amber-200'
          }`}
          data-testid="exposure-window-state"
        >
          <div className="text-[10px] uppercase tracking-wider font-mono text-apex-neutral mb-1">
            Active exposure window
          </div>
          <div className={`text-[12px] font-semibold leading-snug ${
            state.exposure.severity === 'critical' ? 'text-red-700' : 'text-amber-700'
          }`}>
            {state.exposure.label}
          </div>
          {state.exposure.days > 0 && (
            <div className="mt-2 h-1.5 bg-white/60 rounded-full overflow-hidden">
              <motion.div
                animate={{ width: `${Math.min(100, (state.exposure.days / 201) * 100)}%` }}
                transition={{ duration: 0.6, ease: 'easeOut' }}
                className={`h-full rounded-full ${
                  state.exposure.severity === 'critical' ? 'bg-red-500' : 'bg-amber-500'
                }`}
              />
            </div>
          )}
        </motion.div>
      )}

      {/* Evidence count */}
      <div className="flex items-center justify-between pt-2 border-t border-slate-100">
        <div className="text-[10px] uppercase tracking-wider font-mono text-apex-neutral">
          Evidence records created
        </div>
        <motion.div
          key={`ec-${state.evidence_count}`}
          initial={{ scale: 1.15, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-[18px] font-bold tabular-nums font-mono text-apex-ink"
          data-testid="evidence-count-state"
        >
          {state.evidence_count}
        </motion.div>
      </div>

      {/* Verdict — only at phases with a verdict */}
      {state.verdict && (
        <motion.div
          key={`verdict-${idx}`}
          initial={{ opacity: 0, y: 4 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-red-600 rounded-lg p-3 text-white text-[11px] font-semibold leading-relaxed"
          data-testid="governance-verdict"
        >
          {state.verdict}
        </motion.div>
      )}

      {/* Phase 0 — clean state */}
      {idx === 0 && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-emerald-700 text-[12px] font-semibold text-center"
          data-testid="governance-clean-state"
        >
          ✓ All controls enforced · No exposure windows · Clean governance state
        </motion.div>
      )}
    </motion.div>
  );
}

function Scrubber({ phase, totalPhases, timeline, onJump }) {
  // positions: phase 0 sits to the LEFT of the timeline track (baseline pre-event),
  // remaining phases scale by cumulative dwell-days. This prevents marker-0 from
  // overlapping marker-1 when both share dwell=0.
  const stops = useMemo(() => {
    const totalDwell = PHASE_DWELL_DAYS[totalPhases - 1] || 1;
    return PHASE_DWELL_DAYS.slice(0, totalPhases).map((d, i) => ({
      pct: i === 0 ? -3 : (d / totalDwell) * 100,
      idx: i,
      date: i === 0 ? 'Pre' : timeline[i - 1]?.date?.slice(5) || '',
      tag: i === 0 ? 'baseline' : timeline[i - 1]?.tag || 'warning',
    }));
  }, [totalPhases, timeline]);

  const playheadPct = Math.max(0, stops[phase]?.pct ?? 0);

  return (
    <div className="relative pt-7 pb-9" data-testid="replay-scrubber">
      {/* track */}
      <div className="absolute left-0 right-0 top-[36px] h-[6px] rounded-full bg-slate-100" />
      {/* fill */}
      <motion.div
        className="absolute left-0 top-[36px] h-[6px] rounded-full"
        style={{
          background: 'linear-gradient(90deg, #FCD34D 0%, #F59E0B 40%, #DC2626 100%)',
        }}
        animate={{ width: `${playheadPct}%` }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
      />
      {/* playhead */}
      <motion.div
        className="absolute top-[28px] h-[22px] w-[22px] rounded-full bg-white border-2 border-apex-lens shadow-md cursor-pointer"
        animate={{ left: `calc(${playheadPct}% - 11px)` }}
        transition={{ duration: 0.45, ease: 'easeOut' }}
        data-testid="replay-playhead"
      >
        <span className="absolute inset-1 rounded-full bg-apex-lens" />
      </motion.div>
      {/* keyframe markers */}
      {stops.map((s) => {
        const isCrit = s.tag === 'critical';
        const isActive = phase === s.idx;
        const isPast = phase > s.idx;
        return (
          <button
            key={s.idx}
            data-testid={`replay-marker-${s.idx}`}
            onClick={() => onJump(s.idx)}
            className="absolute top-[24px] -translate-x-1/2 group focus:outline-none"
            style={{ left: `${s.pct}%` }}
            aria-label={`Jump to phase ${s.idx}`}
          >
            <div
              className={`w-3 h-3 rotate-45 transition-all ${isActive ? 'scale-125' : 'group-hover:scale-110'}`}
              style={{
                backgroundColor: s.idx === 0 ? '#10B981' : isCrit ? '#DC2626' : '#F59E0B',
                opacity: isPast || isActive ? 1 : 0.55,
              }}
            />
            <div className={`absolute top-[-22px] left-1/2 -translate-x-1/2 text-[10px] font-mono whitespace-nowrap ${isActive ? 'text-apex-ink font-semibold' : 'text-apex-neutral'}`}>
              {s.idx === 0 ? 'baseline' : s.date}
            </div>
            <div className={`absolute top-[20px] left-1/2 -translate-x-1/2 text-[9.5px] uppercase tracking-wider font-mono whitespace-nowrap ${isActive ? 'text-apex-ink' : 'text-slate-400'} flex items-center gap-1`}>
              <ChevronRight size={9} className="opacity-40" />
              ph {s.idx}
            </div>
          </button>
        );
      })}
    </div>
  );
}
